import React, { useEffect, useState, useRef } from 'react';
import { Button, Flex, View } from '@smarterservices/smarter-ui';
import { getOperatingSystem, sendMessageToExtension, systemProfiler } from '../../helpers/helpers';
import DualPane from '../DualPane';
import StepInstructions from '../Common/StepInstructions';
import semver from 'semver/preload';
import { usePaneViewer } from '../PaneViewer';
import { useAppStore } from '../../stores/app';

const SecureSession = ({ stepData, setIsTaskComplete }) => {
  const [os, setOs] = useState(null);
  const [secureSessionReady, setSecureSessionReady] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [blockedProcesses, setBlockedProcesses] = useState([]);
  const [isCheckingProcesses, setIsCheckingProcesses] = useState(false);
  const [hasVersionMismatch, setHasVersionMismatch] = useState(false);
  const { onNext } = usePaneViewer();
  const shouldStopPolling = useRef(false);
  const { apiClient } = useAppStore();

  // Helper function to get the required version based on OS
  const getRequiredVersion = (operatingSystem = os) => {
    if (operatingSystem === 'Mac') {
      return stepData?.mac?.version;
    } else if (operatingSystem === 'Windows') {
      return stepData?.windows?.version;
    }
    return null;
  };

  // Helper function to get the interval based on OS
  const getInterval = (operatingSystem = os) => {
    if (operatingSystem === 'Mac') {
      return stepData?.mac?.interval;
    } else if (operatingSystem === 'Windows') {
      return stepData?.windows?.interval;
    }
    return null;
  };

  // Check if SecureSession is available and has the correct version
  const checkSecureSessionVersion = async (initial = false, operatingSystem = os) => {
    try {
      const response = await sendMessageToExtension({
        message: 'sp_background__nativeMessaging',
        type: 'getVersion'
      });

      const successful = response?.successful;
      const version = response?.data?.version;

      if (successful && version) {
        const secureSessionVersion = response.data.version;
        const requiredVersion = getRequiredVersion(operatingSystem);

        if (requiredVersion) {
          // Do a semver comparison to make sure the found version is EQUAL to the needed
          const isValid = semver.eq(secureSessionVersion, requiredVersion);
          if (!isValid) {
            // The version is not valid, so we will just call to uninstall that version
            await sendMessageToExtension({
              message: 'sp_background__nativeMessaging',
              type: 'uninstall'
            });
            setIsPolling(false);

            if (!initial) {
              // We're in a bad state - version mismatch after download
              try {
                apiClient.addAnnotation({
                  payload: {
                    typeId: 'secure-session-app-version-mismatch',
                    metadata: {
                      downloadVersion: secureSessionVersion,
                      requiredVersion
                    },
                  },
                });
              } catch (error) {
                console.log('Error raising annotation for SecureSession Version Mismatch', { error });
              }
              setHasVersionMismatch(true);
              setSecureSessionReady(false);
              shouldStopPolling.current = true;
            }
          }
          return { available: true, valid: isValid };
        }
      }
    } catch (error) {
      console.log('Error checking SecureSession version:', error);
    }
    return { available: false, valid: false };
  };

  // Poll for SecureSession after download
  const startPollingForSecureSession = async () => {
    setIsPolling(true);
    const pollInterval = 2000; // 2 seconds between checks

    const poll = async () => {
      const result = await checkSecureSessionVersion();

      if (result.available && result.valid) {
        // Success! SecureSession is installed and version is correct
        setSecureSessionReady(true);
        setIsPolling(false);
        return;
      }

      // Only continue polling if we haven't detected a version mismatch
      if (!shouldStopPolling.current) {
        setTimeout(poll, pollInterval);
      }
    };

    poll();
  };

  useEffect(() => {
    if (!stepData) {
      return;
    }

    // Detect the operating system
    const detectedOs = getOperatingSystem(window);
    setOs(detectedOs);

    // Check if the SecureSession app is already available
    const initialCheck = async () => {
      const result = await checkSecureSessionVersion(true, detectedOs);
      if (result.available && result.valid) {
        setSecureSessionReady(true);
      } else {
        setSecureSessionReady(false);
      }
    };

    initialCheck();
  }, [stepData]);

  useEffect(() => {
    if (secureSessionReady) {
      // Now we need to get the processes that are currently running to see if any need to be closed
      const getProcesses = async () => {
        setIsCheckingProcesses(true);
        const response = await sendMessageToExtension({
          message: 'sp_background__secureSessionGetAll',
          onlyNew: false
        });
        if (response && response.processes) {
          const profilerResponse = await systemProfiler(response);
          if (profilerResponse &&
            profilerResponse.blocked &&
            profilerResponse.blocked.processes &&
            profilerResponse.blocked.processes.length > 0) {
            const blocked = profilerResponse.blocked.processes;
            setBlockedProcesses(blocked);
          } else {
            // No blocked processes, allow proceeding
            setBlockedProcesses([]);
            // Start the secure session loop to monitor processes on an interval
            const interval = getInterval();
            await sendMessageToExtension({
              message: 'sp_background__startSecureSessionLoop',
              interval
            });
          }
        }
        setIsCheckingProcesses(false);
      };
      getProcesses();
    }
  }, [secureSessionReady]);

  const getInstructions = () => {
    if (!os) {
      console.log('No OS detected yet');
      return '';
    }

    let instructions = '';

    // Use stepData instructions based on OS
    if (os === 'Mac') {
      instructions = stepData?.mac?.instructions || stepData?.body || '';
    } else if (os === 'Windows') {
      instructions = stepData?.windows?.instructions || stepData?.body || '';
    } else {
      // Fallback for other OS
      throw new Error('Unsupported OS');
    }

    // Return instructions as-is - StepInstructions will handle parsing BlockKit arrays
    return instructions;
  };

  const handleCloseBlockedProcesses = async () => {
    try {
      await sendMessageToExtension({
        message: 'sp_background__nativeMessaging',
        type: 'killProcess',
        data: { items: blockedProcesses }
      });
      // Re-check processes after killing them
      const checkResponse = await sendMessageToExtension({
        message: 'sp_background__secureSessionGetAll',
        onlyNew: false
      });

      try {
        apiClient.addAnnotation({
          payload: {
            typeId: 'secure-session-app-check',
            metadata: {
              blockedProcesses
            },
          },
        });
      } catch (error) {
        console.log('Error raising annotation for SecureSession App Check', { error });
      }

      if (checkResponse && checkResponse.processes) {
        const profilerResponse = await systemProfiler(checkResponse);
        if (profilerResponse &&
          profilerResponse.blocked &&
          profilerResponse.blocked.processes &&
          profilerResponse.blocked.processes.length > 0) {
          // Still have blocked processes
          setBlockedProcesses(profilerResponse.blocked.processes);
        } else {
          // All processes closed successfully
          setBlockedProcesses([]);
          // Start the secure session loop to monitor processes on an interval
          const interval = getInterval();
          await sendMessageToExtension({
            message: 'sp_background__startSecureSessionLoop',
            interval
          });
        }
      }
    } catch (error) {
      console.error('Error killing processes:', error);
    }
  };

  const handleDownload = async () => {
    if (!os) {
      console.log('No OS detected yet');
      return;
    }

    // Reset version mismatch state in case of retry
    setHasVersionMismatch(false);
    shouldStopPolling.current = false;

    let url, filename;

    if (os === 'Mac') {
      url = stepData?.mac?.installerUrl;
      filename = stepData?.mac?.filename;
    } else if (os === 'Windows') {
      url = stepData?.windows?.installerUrl;
      filename = stepData?.windows?.filename;
    }

    if (url) {
      try {
        const response = await sendMessageToExtension({
          message: 'sp_background__downloadFile',
          url,
          filename
        });

        if (response?.success && response?.downloadId) {
          try {
            await sendMessageToExtension({
              message: 'sp_background__openFile',
              downloadId: response.downloadId
            });

            // Start polling for SecureSession to be available
            startPollingForSecureSession();
          } catch (error) {
            console.log('Open download error:', error.message);
          }
        }
      } catch (error) {
        console.error('Download error:', error);
      }
    }
  };

  const proceedWithOnboarding = () => {
    setIsTaskComplete(true);
    onNext();
  };

  const instructions = getInstructions();
  const buttonText = stepData?.buttonText || 'Download SecureSession App';

  return (
    <>
      {!stepData && <div>No stepData</div>}
      {!os && <div>Waiting for OS detection...</div>}
      {secureSessionReady === null && !isPolling && <div>Checking SecureSession status...</div>}
      {stepData && os && (secureSessionReady !== null || isPolling) && (
        <DualPane
          leftPane={
            <StepInstructions instructions={instructions} />
          }
          rightPane={
            <Flex
              direction="column"
              alignItems="center"
              justifyItems="start"
              gap="large"
              height="100%"
            >
              {isPolling && !hasVersionMismatch && (
                <View as="div" width="100%" textAlign="center">
                  Waiting for SecureSession to open...
                </View>
              )}
              {hasVersionMismatch && (
                <View as="div" width="100%">
                  <View as="div" margin="0 0 medium 0" textAlign="center">
                    <strong>Version Mismatch Detected</strong>
                  </View>
                  <View as="div" margin="0 0 medium 0">
                    The SecureSession app version installed on your computer does not match the version required by your institution.
                    This is a configuration issue that requires support assistance.
                  </View>
                  <View as="div" margin="0 0 medium 0">
                    <strong>Please contact support for assistance.</strong>
                  </View>
                </View>
              )}
              {!isPolling && !secureSessionReady && !hasVersionMismatch && (
                <View as="div" width="100%" textAlign="center">
                  <Button
                    color="primary"
                    size="medium"
                    margin="medium 0"
                    onClick={handleDownload}
                  >
                    {buttonText}
                  </Button>
                </View>
              )}
              {!isPolling && secureSessionReady && isCheckingProcesses && (
                <View as="div" width="100%" textAlign="center">
                  Checking for blocked processes...
                </View>
              )}
              {!isPolling && secureSessionReady && !isCheckingProcesses && blockedProcesses.length > 0 && (
                <View as="div" width="100%">
                  <View as="div" margin="0 0 medium 0">
                    <strong>The following processes must be closed before proceeding:</strong>
                  </View>
                  <View as="ul" margin="0 0 medium 0">
                    {blockedProcesses.map((process, index) => (
                      <li key={index}>{process.name}</li>
                    ))}
                  </View>
                  <View as="div" width="100%" textAlign="center">
                    <Button
                      color="primary"
                      size="medium"
                      onClick={handleCloseBlockedProcesses}
                    >
                      Close Blocked Processes
                    </Button>
                  </View>
                </View>
              )}
              {!isPolling && secureSessionReady && !isCheckingProcesses && blockedProcesses.length === 0 && (
                <View as="div" width="100%" textAlign="center">
                  <View as="div" margin="0 0 medium 0">
                    Everything is complete, you may proceed
                  </View>
                  <View as="div" width="100%" textAlign="center">
                    <Button
                      color="primary"
                      size="medium"
                      onClick={proceedWithOnboarding}
                    >
                      Proceed
                    </Button>
                  </View>
                </View>
              )}
            </Flex>
          }
        />
      )}
    </>
  );
};

export default SecureSession;
